"use client";

import { useCallback, useState } from "react";

/**
 * The props a `ConfirmDialog` (or `DeleteConfirmDialog`) needs to drive it.
 * Pass it straight through: `<DeleteConfirmDialog {...confirm.dialogProps} …/>`.
 */
export interface ConfirmDialogState {
  isOpen: boolean;
  isPending: boolean;
  onConfirm: () => void;
  onOpenChange: (isOpen: boolean) => void;
}

export interface UseConfirmDialogResult<T> {
  /** Spread onto the dialog. */
  dialogProps: ConfirmDialogState;
  /** Open the dialog for a subject (the row being deleted, say). */
  request: (subject: T) => void;
  /** The subject awaiting confirmation, or null. */
  subject: T | null;
}

/**
 * Confirmation state for an action that runs against some subject: which
 * subject is awaiting confirmation, whether the action is in flight, and the
 * open/close plumbing.
 *
 * The pending flag is cleared here rather than by the caller, so a confirm that
 * throws cannot leave the dialog stuck in its pending state.
 */
export function useConfirmDialog<T>(onConfirm: (subject: T) => Promise<void> | void): UseConfirmDialogResult<T> {
  const [subject, setSubject] = useState<T | null>(null);
  const [isPending, setIsPending] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (subject === null) return;
    setIsPending(true);
    try {
      await onConfirm(subject);
    } finally {
      setIsPending(false);
      setSubject(null);
    }
  }, [subject, onConfirm]);

  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) setSubject(null);
  }, []);

  return {
    dialogProps: {
      isOpen: subject !== null,
      isPending,
      onConfirm: handleConfirm,
      onOpenChange: handleOpenChange,
    },
    request: setSubject,
    subject,
  };
}
